import { XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { formatDateIST } from "@/lib/dateUtils";
import { calculatePnl } from "@/utils/pnl";

export const CloseTradeModal = ({
  isOpen,
  onClose,
  trade,
  closeForm,
  setCloseForm,
  onCloseTrade,
  isClosing,
}) => {
  if (!trade) return null;

  // Calculate P&L preview using correct multiplier
  const getPnLPreview = () => {
    if (!closeForm.exit_price) return null;
    
    const exitPrice = parseFloat(closeForm.exit_price);
    const hasPartialCloses = trade.exit_legs && trade.exit_legs.length > 0;
    const effectiveLots = hasPartialCloses 
      ? (trade.remaining_lots ?? trade.num_lots) 
      : (trade.num_lots || 1);
    
    const { pnlPoints, pnlAmount } = calculatePnl({
      direction: trade.direction,
      entryPrice: trade.entry_price,
      exitPrice,
      numLots: effectiveLots,
      trade,
    });
    
    // If partial closes exist, add partial P&L to show total
    const partialPnl = hasPartialCloses ? (trade.partial_pnl_amount || 0) : 0;
    
    return { pnlPoints, pnlAmount, totalPnl: pnlAmount + partialPnl, partialPnl, hasPartialCloses, effectiveLots };
  };

  const pnlPreview = getPnLPreview();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-charcoal border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold flex items-center gap-2">
            <XCircle className="w-5 h-5 text-loss" />
            Close Trade - {trade.symbol}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Enter exit details to close this {trade.direction} position
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Trade Summary */}
          <div className="bg-black/20 p-3 rounded-md text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="text-slate-500">Direction:</span>{" "}
                <span className={trade.direction === "LONG" ? "text-profit" : "text-loss"}>
                  {trade.direction}
                </span>
              </div>
              <div>
                <span className="text-slate-500">Entry Price:</span>{" "}
                ₹{trade.entry_price?.toLocaleString('en-IN')}
              </div>
              <div>
                <span className="text-slate-500">Quantity:</span>{" "}
                {trade.num_lots || 0} × {trade.lot_size || 1}
              </div>
              <div>
                <span className="text-slate-500">Entry Date:</span>{" "}
                {formatDateIST(trade.entry_date)}
              </div>
            </div>
            {/* Show partial close info if applicable */}
            {trade.exit_legs && trade.exit_legs.length > 0 && (
              <div className="mt-2 pt-2 border-t border-white/10">
                <div className="flex justify-between">
                  <span className="text-yellow-400 text-xs">Partially closed: {trade.total_exited_lots || 0} lots exited</span>
                  <span className="text-cyan-400 text-xs font-medium">Remaining: {trade.remaining_lots ?? trade.num_lots} lots</span>
                </div>
                {trade.partial_pnl_amount != null && (
                  <div className="flex justify-between mt-1">
                    <span className="text-slate-500 text-xs">Booked P&L:</span>
                    <span className={`text-xs font-medium ${trade.partial_pnl_amount >= 0 ? 'text-profit' : 'text-loss'}`}>
                      ₹{trade.partial_pnl_amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300">Exit Price *</Label>
              <Input
                type="number"
                step="0.01"
                value={closeForm.exit_price}
                onChange={(e) => setCloseForm(prev => ({ ...prev, exit_price: e.target.value }))}
                className="bg-black/20 border-white/10 text-white mt-1"
                data-testid="close-exit-price"
              />
            </div>
            <div>
              <Label className="text-slate-300">Exit Date *</Label>
              <Input
                type="datetime-local"
                value={closeForm.exit_date}
                onChange={(e) => setCloseForm(prev => ({ ...prev, exit_date: e.target.value }))}
                className="bg-black/20 border-white/10 text-white mt-1"
                data-testid="close-exit-date"
              />
            </div>
          </div>

          {/* P&L Preview */}
          {pnlPreview && (
            <div className="bg-black/30 p-3 rounded-md">
              {pnlPreview.hasPartialCloses ? (
                <>
                  <p className="text-slate-400 text-sm">P&L for remaining {pnlPreview.effectiveLots} lot{pnlPreview.effectiveLots > 1 ? 's' : ''}:</p>
                  <p className={`text-lg font-bold ${pnlPreview.pnlAmount >= 0 ? 'text-profit' : 'text-loss'}`}>
                    ₹{pnlPreview.pnlAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    <span className="text-sm font-normal ml-2">({pnlPreview.pnlPoints.toFixed(2)} pts)</span>
                  </p>
                  <div className="mt-2 pt-2 border-t border-white/10">
                    <p className="text-slate-400 text-xs mb-1">Total P&L (booked + remaining):</p>
                    <p className={`text-2xl font-bold ${pnlPreview.totalPnl >= 0 ? 'text-profit' : 'text-loss'}`}>
                      ₹{pnlPreview.totalPnl.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-slate-400 text-sm">Estimated P&L:</p>
                  <p className={`text-2xl font-bold ${pnlPreview.pnlAmount >= 0 ? 'text-profit' : 'text-loss'}`}>
                    ₹{pnlPreview.pnlAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    <span className="text-sm font-normal ml-2">
                      ({pnlPreview.pnlPoints.toFixed(2)} pts)
                    </span>
                  </p>
                </>
              )}
            </div>
          )}

          <div>
            <Label className="text-slate-300">Exit Comments</Label>
            <Textarea
              value={closeForm.exit_comments}
              onChange={(e) => setCloseForm(prev => ({ ...prev, exit_comments: e.target.value }))}
              className="bg-black/20 border-white/10 text-white mt-1"
              placeholder="Reason for closing..."
              data-testid="close-comments"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button 
            variant="outline" 
            onClick={() => onClose(false)} 
            className="border-white/10 text-slate-300"
          >
            Cancel
          </Button>
          <Button 
            onClick={onCloseTrade} 
            disabled={isClosing} 
            className="bg-loss hover:bg-loss/80 text-white"
          >
            {isClosing ? "Closing..." : "Close Trade"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CloseTradeModal;
